/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Tippy from "@tippyjs/react";
import Icon from "react-icons-kit";
import { arrows_left } from "react-icons-kit/linea/arrows_left";
import { basic_compass } from "react-icons-kit/linea/basic_compass";
import { basic_smartphone } from "react-icons-kit/linea/basic_smartphone";
import { basic_mail } from "react-icons-kit/linea/basic_mail";
import { basic_world } from "react-icons-kit/linea/basic_world";
import { basic_share } from "react-icons-kit/linea/basic_share";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { MDBBtn } from "mdb-react-ui-kit";
import DOMPurify from "dompurify";
import Thermometer from "react-thermometer-component";
import { LazyLoadImage } from "react-lazy-load-image-component";
import PledgeModal from "../../../../components/sites/home/pledges/PledgeModal";
import Share from "../../../../components/sites/home/reusable/Share";

const CustomTextField = styled(TextField)`
  & label.Mui-focused {
    color: #007c16;
  }
  & .MuiOutlinedInput-root {
    &.Mui-focused fieldset {
      border: 1px solid;
      border-color: #007c16;
    }
  }
  &:hover {
    & label {
      color: #007c16;
    }
    & .MuiOutlinedInput-root {
      fieldset {
        border-color: #007c16;
      }
    }
  }
`;

const IndividualPledge = ({
  userData,
  setLoggedInUserData,
  orgName,
  orgObj,
  pledgesArray,
}) => {
  // get tagNumber from URL
  const { tagNumber } = useParams();

  // use t function where ever hard coded text is used
  const { t } = useTranslation();

  // navigate
  const navigate = useNavigate();

  // tippy content
  const content = t("pledge.back_to_pledges");

  // navigation on back button click
  const handleGoBack = () => {
    navigate(`/sites/${orgName}/pledges`);
  };

  // individual pledge obj
  const [pledgeObj, setPledgeObj] = useState(() => {
    if (pledgesArray) {
      const selectedPledge = pledgesArray.find(
        (pledge) => pledge.tagNumber === Number(tagNumber)
      );

      return selectedPledge || null;
    }
  });

  // do the setup so that this page is ready to always show the individual pledge
  useEffect(() => {
    if (pledgesArray) {
      const selectedPledge = pledgesArray.find(
        (pledge) => pledge.tagNumber === Number(tagNumber)
      );
      setPledgeObj(selectedPledge || null);
    }
  }, [tagNumber, pledgesArray]);

  // pledge amount
  const [pledgeAmount, setPledgeAmount] = useState("");

  // handle pledge amount
  const handlePledgeAmount = (value) => {
    if (value) {
      setPledgeAmount(value);
    } else {
      setPledgeAmount("");
    }
  };

  // pledge modal
  const [pledgeModal, setPledgeModal] = useState(false);

  // pledge submit
  const handlePledgeSubmit = (e) => {
    e.preventDefault();
    if (
      pledgeAmount &&
      parseFloat(pledgeAmount) >= parseFloat(pledgeObj.minimumAmount)
    ) {
      setPledgeModal(true);
    }
  };

  // share
  const [sharePopup, setSharePopup] = useState(false);
  const [shareLink, setShareLink] = useState(null);

  const handleShare = (qrCode) => {
    setSharePopup(true); // don't toggle
    setShareLink(qrCode); // qrCode contains the complete URL that needs to be shared
  };

  // selector btns
  const [step, setStep] = useState("pledge-details");

  // is image loaded?
  const [isLoaded, setIsLoaded] = useState(false);

  // scroll to top
  useEffect(() => {
    document.querySelector(".home-content")?.scrollTo(0, 0);
  }, []);

  return (
    <>
      {/* pledgeModal */}
      {pledgeModal && (
        <PledgeModal
          setPledgeModal={setPledgeModal}
          pledgeObj={pledgeObj}
          orgObj={orgObj}
          orgName={orgName}
          pledgeAmount={pledgeAmount}
          handlePledgeAmount={handlePledgeAmount}
          userData={userData}
          setLoggedInUserData={setLoggedInUserData}
        />
      )}

      {/* share popup */}
      {sharePopup && shareLink && (
        <Share
          shareLink={shareLink}
          setSharePopup={setSharePopup}
          setShareLink={setShareLink}
        />
      )}

      {/* pledgeObj is available */}
      {pledgeObj ? (
        <div className="cards-container single-card">
          <div className="custom-card single-card">
            {/* back icon */}
            {content && (
              <div style={{ background: "#f8f8f8" }}>
                <Tippy content={content}>
                  <button
                    type="button"
                    style={{
                      backgroundColor: "transparent",
                      color: "#9b9b9b",
                      border: "none",
                      outline: "none",
                      paddingBottom: "8px",
                    }}
                    onClick={handleGoBack}
                  >
                    <Icon icon={arrows_left} size={48} />
                  </button>
                </Tippy>
              </div>
            )}

            {/* header */}
            <header className="card-header">
              {/* leftside */}
              <div className="card-leftside single-card">
                <div className="card-logo">
                  <LazyLoadImage
                    src={pledgeObj.campaignImage}
                    alt="campaign-logo"
                    effect="blur"
                  />
                </div>
                <div className="title-and-city-country">
                  <div className="organization-name">{pledgeObj.name}</div>
                  <h5
                    className="pledge-org-name"
                    onClick={() => navigate(`/sites/${orgName}`)}
                  >
                    {orgObj.name}
                  </h5>
                  <div className="city-and-country single-card">
                    {pledgeObj.qrCode && (
                      <Tippy content={t("pledge.share")}>
                        <span
                          style={{
                            color: "#007c16",
                            cursor: "pointer",
                          }}
                          onClick={() => handleShare(pledgeObj.qrCode)}
                        >
                          <Icon icon={basic_share} size={24} />
                        </span>
                      </Tippy>
                    )}
                  </div>
                </div>
              </div>

              {/* donation box */}
              <div className="donation-box">
                <div className="donation-text">
                  {t("pledge.enter_pledge_amount")}
                  <span
                    className={`minimum-amount-msg${
                      pledgeAmount !== "" &&
                      parseFloat(pledgeAmount) <
                        parseFloat(pledgeObj.minimumAmount)
                        ? " red"
                        : ""
                    }`}
                    style={{ marginLeft: "10px" }}
                  >
                    {t("CampaignDetails.minimum_amount")}{" "}
                    {pledgeObj.currencySymbol}
                    {pledgeObj.minimumAmount}
                  </span>
                </div>
                <form className="donation-form" onSubmit={handlePledgeSubmit}>
                  <div className="input-and-btn-div">
                    <CustomTextField
                      variant="outlined"
                      fullWidth
                      autoComplete="off"
                      size="small"
                      value={pledgeAmount}
                      onChange={(e) => {
                        let newValue = e.target.value;
                        if (isNaN(newValue) || parseFloat(newValue) < 0) {
                          newValue = "0";
                        }
                        handlePledgeAmount(newValue);
                      }}
                      onKeyDown={(e) => {
                        const parts = e.target.value.split(".");

                        // Allow numeric keys, Backspace, Delete, navigation keys, and decimal point
                        if (
                          !/[0-9]/.test(e.key) &&
                          ![
                            "Backspace",
                            "Enter",
                            "Delete",
                            "ArrowLeft",
                            "ArrowRight",
                            "ArrowUp",
                            "ArrowDown",
                            ".",
                          ].includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                        // Prevent typing more than two decimal places
                        else if (
                          e.key !== "Backspace" &&
                          parts.length > 1 &&
                          parts[1].length >= 2 &&
                          /[0-9]/.test(e.key)
                        ) {
                          e.preventDefault();
                        }
                        // Prevent typing second decimal point
                        else if (
                          e.key === "." &&
                          e.target.value.includes(".")
                        ) {
                          e.preventDefault();
                        }
                      }}
                      placeholder={t("pledge.pledge_amount")}
                      helperText={
                        pledgeAmount !== "" &&
                        parseFloat(pledgeAmount) <
                          parseFloat(pledgeObj.minimumAmount)
                          ? `${t("CampaignDetails.amount_error_msg")} ${
                              pledgeObj.minimumAmount
                            }`
                          : ""
                      }
                      error={
                        pledgeAmount !== "" &&
                        parseFloat(pledgeAmount) <
                          parseFloat(pledgeObj.minimumAmount)
                      }
                    />
                    <MDBBtn
                      type="submit"
                      className="donate-btn"
                      disabled={
                        !pledgeAmount ||
                        parseFloat(pledgeAmount) <
                          parseFloat(pledgeObj.minimumAmount)
                      }
                    >
                      {t("pledge.pledge")}
                    </MDBBtn>
                  </div>
                </form>
              </div>
            </header>

            {/* tabination */}
            <div className="campaigns-or-contact">
              <button
                type="button"
                onClick={() => setStep("pledge-details")}
                className={`selector-btns${
                  step === "pledge-details" ? " active" : ""
                }`}
              >
                {t("pledge.pledge_details").toUpperCase()}
              </button>
              <button
                type="button"
                onClick={() => setStep("contact")}
                className={`selector-btns${
                  step === "contact" ? " active" : ""
                }`}
              >
                {t("OrganizationDetails.contact")}
              </button>
            </div>

            {/* Pledge Details Section */}
            {step === "pledge-details" && (
              <>
                <div className="pledge-details-container">
                  <div className="description-banner-wrapper">
                    {/* Campaign Description */}
                    <div className="campaign-container">
                      <div
                        className="campaign-description"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(pledgeObj?.description),
                        }}
                      />
                    </div>

                    {/* Banner Image (if available) */}
                    {pledgeObj.bannerImage && (
                      <div
                        className="banner-img-container"
                        style={{
                          margin: "20px auto",
                          background: isLoaded ? "transparent" : "#f7f7f7",
                        }}
                      >
                        <img
                          src={pledgeObj.bannerImage}
                          alt="Pledge Banner"
                          onLoad={() => {
                            setTimeout(() => {
                              setIsLoaded(true);
                            }, 500);
                          }}
                          style={{
                            display: isLoaded ? "block" : "none",
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* thermometer details container */}
                  <div className="thermometer-details-container">
                    {/* Thermometer (if showProgress is true) */}
                    {pledgeObj.showProgress && (
                      <div className="thermometer-div">
                        <Thermometer
                          theme="light"
                          value={
                            parseFloat(pledgeObj.fulfillmentPercentage) > 100
                              ? 100 + "%"
                              : pledgeObj.fulfillmentPercentage.toFixed(2) + "%"
                          }
                          max={100}
                          size="large"
                          height={300}
                        />
                      </div>
                    )}

                    {/* Pledge Progress Details */}
                    <div className="pledge-details">
                      <div className="pledge-info">
                        <span>{t("pledge.target_amount")}:</span>
                        <strong>
                          {pledgeObj.currencySymbol}
                          {pledgeObj.targetAmount.toLocaleString()}
                        </strong>
                      </div>

                      {pledgeObj.showProgress && (
                        <div className="pledge-info">
                          <span>{t("pledge.pledged_amount")}:</span>
                          <strong>
                            {pledgeObj.currencySymbol}
                            {pledgeObj.pledgedAmount.toLocaleString()}
                          </strong>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* contact */}
            {step === "contact" && (
              <div className="contact-box">
                <h3 className="get-in-touch-heading">
                  {t("OrganizationDetails.get_in_touch")}
                </h3>
                <div className="contact-info-box">
                  {/* name */}
                  <h6>{orgObj.name}</h6>
                  {/* streetAddress and unit no */}
                  <div className="p-row">
                    <div>
                      <Icon icon={basic_compass} size={16} />
                    </div>
                    <div className="p-row-text">
                      {orgObj.streetAddress} {orgObj.unitNumber}
                    </div>
                  </div>
                  {/* city province and postal code */}
                  <div className="p-row">
                    <div className="empty-div"></div>
                    <div className="p-row-text">
                      {orgObj.city} {orgObj.provinceState}{" "}
                      {orgObj.postalZipCode}
                    </div>
                  </div>
                  {/* country */}
                  <div className="p-row">
                    <div className="empty-div"></div>
                    <div className="p-row-text">{orgObj.country}</div>
                  </div>
                  {/* phone */}
                  <div className="p-row">
                    <div>
                      <Icon icon={basic_smartphone} size={16} />
                    </div>
                    <div className="p-row-text">{orgObj.phone}</div>
                  </div>
                  {/* email */}
                  <div className="p-row" style={{ overflow: "hidden" }}>
                    <div>
                      <Icon icon={basic_mail} size={16} />
                    </div>
                    <div className="p-row-text">
                      {orgObj.email ? (
                        <a href={`mailto:${orgObj.email}`}>{orgObj.email}</a>
                      ) : (
                        <span>{t("Content.not_available")}</span>
                      )}
                    </div>
                  </div>
                  {/* website */}
                  <div className="p-row" style={{ overflow: "hidden" }}>
                    <div>
                      <Icon icon={basic_world} size={16} />
                    </div>
                    <div className="p-row-text">
                      {orgObj.website ? (
                        <a href={orgObj.website} target="blank">
                          {orgObj.website}
                        </a>
                      ) : (
                        <span>{t("Content.not_available")}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* back button */}
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                padding: "20px",
              }}
            >
              <button
                type="button"
                className="go-back-to-campaign-selection-btn"
                onClick={handleGoBack}
              >
                {content}
              </button>
            </div>
          </div>
        </div>
      ) : (
        // show error if no pledge
        <main className="info-content-div" style={{ height: "100%" }}>
          <div className="error-msg" style={{ marginTop: 0 }}>
            {t("pledge.no_pledge_found")}
          </div>
        </main>
      )}
    </>
  );
};

export default IndividualPledge;
